import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { LuChartPie } from 'react-icons/lu';

import { Allocation, AllocationType, Strategy } from '@ndvr/portfolio-api';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

import { formatWeightForDisplay } from '../../shared-utils';
import { useCreateAllocationWithStrategies, useUpdateAllocationWithStrategies } from '../allocation-queries';
import { AllocationDraft } from '../cloneAllocationType';
import { createModelFormSchema, ModelFormValues } from './model-form-schema';
import { ModelFormBody } from './ModelFormBody';
import { toStrategyElementsRequest } from './toStrategyElementsRequest';
import { useModelStrategyElements } from './useModelStrategyElements';

interface EditModelDialogProps {
    allocation?: Allocation | AllocationDraft;
    onSaved: () => void;
    onCancel: () => void;
    open: boolean;
    canCreateSharedAllocation?: boolean;
    existingAllocationNames?: string[];
    availableStrategies: Strategy[];
}

export function EditModelDialog({
    allocation,
    onSaved,
    onCancel,
    open,
    canCreateSharedAllocation = false,
    existingAllocationNames = [],
    availableStrategies,
}: EditModelDialogProps) {
    const nameInputRef = useRef<HTMLInputElement | null>(null);

    const { mutate: createAllocationWithStrategies, isPending: isCreating } = useCreateAllocationWithStrategies();
    const { mutate: updateAllocationWithStrategies, isPending: isUpdating } = useUpdateAllocationWithStrategies();
    const isSaving = isCreating || isUpdating;

    const getDefaultValues = useCallback((allocationData?: Allocation | AllocationDraft): ModelFormValues => {
        const strategyElements = (allocationData?.strategyAllocations || [])
            .map((sa) => ({
                mode: 'existing' as const,
                weight: formatWeightForDisplay(sa.weight),
                existingStrategyGUID: sa.strategyGUID,
            }))
            .sort((a, b) => parseFloat(b.weight) - parseFloat(a.weight));

        return {
            allocationName: allocationData?.allocationName || '',
            allocationType:
                allocationData?.allocationType === AllocationType.SHARED
                    ? AllocationType.SHARED
                    : AllocationType.PERSONAL,
            strategyElements,
        };
    }, []);

    const formSchema = createModelFormSchema(
        existingAllocationNames,
        availableStrategies.map((s) => s.strategyName),
        availableStrategies,
        allocation?.allocationName,
    );

    const form = useForm<ModelFormValues>({
        resolver: yupResolver(formSchema),
        defaultValues: getDefaultValues(allocation),
        mode: 'onSubmit',
    });

    useEffect(() => {
        form.reset(getDefaultValues(allocation));
    }, [allocation, form, getDefaultValues]);

    useEffect(() => {
        if (open) {
            form.reset(getDefaultValues(allocation));
            const timer = setTimeout(() => nameInputRef.current?.focus(), 100);
            return () => clearTimeout(timer);
        }
    }, [open, allocation, form, getDefaultValues]);

    const { commitNew } = useModelStrategyElements(form);

    const isAdding = !allocation?.allocationGUID;
    const modalTitle = isAdding ? 'New Model' : 'Edit Model';

    const handleSave = (data: ModelFormValues) => {
        const strategyElements = toStrategyElementsRequest(data.strategyElements);

        if (allocation?.allocationGUID) {
            updateAllocationWithStrategies(
                {
                    allocationGUID: allocation.allocationGUID,
                    request: {
                        allocationName: data.allocationName,
                        allocationGroupName: allocation.allocationGroupName,
                        strategyElements,
                    },
                },
                { onSuccess: onSaved },
            );
        } else {
            createAllocationWithStrategies(
                { allocationName: data.allocationName, allocationType: data.allocationType, strategyElements },
                { onSuccess: onSaved },
            );
        }
    };

    return (
        <Dialog open={open} onOpenChange={(newOpen) => !newOpen && onCancel()}>
            <DialogContent className="flex max-h-[85vh] max-w-6xl flex-col bg-white tracking-[-0.02em]">
                <DialogHeader className="shrink-0">
                    <DialogTitle className="flex items-center gap-2">
                        <LuChartPie className="h-5 w-5 text-gray-600" />
                        {modalTitle}
                    </DialogTitle>
                    <DialogDescription></DialogDescription>
                </DialogHeader>
                <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 min-h-0 flex-auto overflow-y-auto px-1">
                    <form id="model-form" onSubmit={form.handleSubmit(handleSave)}>
                        <ModelFormBody
                            form={form}
                            showTypeSelector={canCreateSharedAllocation}
                            canChangeType={isAdding}
                            availableStrategies={availableStrategies}
                            existingStrategyNames={availableStrategies.map((s) => s.strategyName)}
                            onCommitNewStrategy={commitNew}
                            nameInputRef={nameInputRef}
                        />
                    </form>
                </div>
                <DialogFooter className="shrink-0">
                    <Button
                        variant="outline"
                        className="font-medium"
                        type="button"
                        onClick={onCancel}
                        disabled={isSaving}
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="default"
                        type="submit"
                        className="font-medium"
                        form="model-form"
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
